import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import { useQuery } from "@tanstack/react-query";
import { useProfileContext } from "Auth/contexts";
import { parseJWT } from "Auth/utils";
import { IBand, IBandUser } from "Bands/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { API_ENDPOINTS } from "shared/constants";
import { APIService } from "shared/services";

export const intercomSettings = {
  api_base: "https://api-iam.intercom.io",
  app_id: "p2vmu46h",
};

export const useIntercom = () => {
  const { currentUser } = useProfileContext();

  const { data: intercomUser } = useQuery({
    queryKey: ["intercomUser"],
    queryFn: () =>
      currentUser?.id
        ? APIService.get({
            path: API_ENDPOINTS.USER(currentUser?.id as string),
          })
        : null,
    select: data => data?.user?.[API_ENDPOINTS.USER(currentUser?.id as string)]?.attributes,
    refetchInterval: data => {
      const token =
        data?.user?.[API_ENDPOINTS.USER(currentUser?.id as string)]?.attributes?.intercomToken;

      if (!token) return false;

      const { exp } = parseJWT(token);
      const expiresAt = new Date(exp * 1000);
      const now = new Date();
      const timeDiff = expiresAt.getTime() - now.getTime();

      return timeDiff > 0 ? timeDiff : false;
    },
  });

  const [bandUser, setBandUser] = useState<IBandUser | null>(null);
  const [band, setBand] = useState<IBand | null>(null);
  const bandUserLoaded = useRef(false);

  const user = useMemo(
    () =>
      intercomUser
        ? {
            name: intercomUser.firstname + " " + intercomUser.lastname,
            user_id: API_ENDPOINTS.USER(intercomUser.id),
            intercom_user_jwt: intercomUser.intercomToken,
          }
        : {},
    [intercomUser]
  );

  const loadedBandUser = useMemo(() => {
    if (!bandUser || !band) return undefined;
    return {
      "Band ID": API_ENDPOINTS.BAND(band.id),
      "Band Name": band.title,
    };
  }, [band, bandUser]);

  useEffect(() => {
    shutdown();
    Intercom({ ...intercomSettings, ...user });
  }, [user]);

  useEffect(() => {
    if (loadedBandUser && !bandUserLoaded.current) {
      Intercom({ ...intercomSettings, ...user, ...loadedBandUser });
      bandUserLoaded.current = true;
    }
  }, [loadedBandUser, user, bandUserLoaded]);

  return {
    setBand,
    setBandUser,
  };
};
